/**
 * Roberts — no-reload submit for lead forms
 * (newsletter, email signup, contact, consultation, blog comment, booking).
 *
 * Opt-in: add class "js-ajax-form" to a Shopify {% form %} that has a UNIQUE id.
 *
 * Technique — fetch POST:
 *   The earlier hidden-iframe approach is blocked on this store by
 *   `Content-Security-Policy: frame-ancestors 'none'`: the post-submit storefront page
 *   refuses to render inside the frame, so success could not be read from the iframe and
 *   the code fell back to a full page reload every time. Instead we POST the form with
 *   fetch(). Shopify's /contact accepts a normal multipart FormData POST and 302-redirects
 *   to `?<type>_posted=true` on success; fetch follows that redirect and exposes the landing
 *   URL as response.url, which we test for the success marker. The top page never navigates.
 *   We intercept the submit in the CAPTURE phase and stopImmediatePropagation() so the theme's
 *   own form handler (which reloads the page) never runs. Anything that isn't a clear success
 *   (field errors, bot /challenge) falls back to a native full submit so Shopify's own
 *   server-rendered result shows exactly as before.
 *
 * Per-form hooks (added in the Liquid):
 *   [data-rj-success]  success element inside the <form>, rendered hidden — we reveal it.
 *                      In "collapse" mode everything except this message (and its container path
 *                      up to the form) is hidden, so the form is visually replaced by it.
 *   class js-ajax-toast  optional: "toast" mode — keep the form usable (newsletters), just show
 *                        message + reset. Without it the default is "collapse".
 *
 * No JS / script failure → the submit is never intercepted → normal reload with Shopify's
 * server-rendered success; revealOnLoad() re-shows the message after that reload (graceful
 * degradation).
 */
(function () {
  var SUCCESS_RE = /[?&](contact_posted|customer_posted|comment_posted)=true/;
  var submitting = new WeakSet();
  // The form always stays visible on success; the confirmation shows below it and
  // auto-hides after this many ms. (Change this one number to tune the duration.)
  var AUTO_HIDE_MS = 10000;

  // Reveal a form's [data-rj-success] message. Toast for ALL forms: the form stays
  // visible and the confirmation is shown at the BOTTOM of the form (below the submit
  // button), then auto-hidden after AUTO_HIDE_MS. (Forms no longer collapse.)
  function revealSuccess(form) {
    var success = form.querySelector('[data-rj-success]');
    form.reset();
    if (!success) return false;
    // Move the message to the end of the form so it always appears below the fields/button,
    // regardless of where it's authored in the markup.
    if (form.lastElementChild !== success) form.appendChild(success);
    success.hidden = false;
    if (typeof success.focus === 'function') success.focus();
    // Auto-hide so the confirmation doesn't linger; a new submit re-shows and re-times it.
    if (success.__rjHideTimer) clearTimeout(success.__rjHideTimer);
    success.__rjHideTimer = setTimeout(function () {
      success.hidden = true;
      success.__rjHideTimer = null;
    }, AUTO_HIDE_MS);
    return true;
  }

  // Native full submit so Shopify's server-rendered errors / challenge appear exactly as
  // they do without JS. form.submit() does NOT fire the submit event, so the capture
  // listener below never re-intercepts it.
  function fallbackSubmit(form) {
    form.submit();
  }

  // Reload fallback: when the top page reloaded on submit (JS disabled, or a network error
  // sent us down fallbackSubmit), Shopify appends ?<type>_posted=true plus a #FormId hash.
  // Reveal that form's success message so it shows even without the fetch path.
  function revealOnLoad() {
    if (!SUCCESS_RE.test(window.location.search)) return;
    var form = null;
    var hash = window.location.hash;
    if (hash && hash.length > 1) {
      var el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) form = el.tagName === 'FORM' ? el : el.closest('form');
    }
    if (!form) form = document.querySelector('form.js-ajax-form');
    if (form && form.classList.contains('js-ajax-form')) revealSuccess(form);
  }

  // CAPTURE phase + stopImmediatePropagation so we beat the theme's own form handler.
  // Horizon's base form JS otherwise reloads the page. Running first in the capture phase and
  // stopping propagation neutralizes it; then we POST the form with fetch() ourselves, so the
  // top page never navigates.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains('js-ajax-form')) return;
      // Let native HTML5 validation surface invalid fields (leave the event alone so the
      // browser shows the validation UI and the theme behaves normally for invalid forms).
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
      if (!form.id) return; // opt-in contract: need a stable id

      // Take full control of this submit: cancel the default navigation AND stop the theme's
      // own submit handler from running.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (submitting.has(form)) return; // one submit at a time per form
      submitting.add(form);

      // Disable the submit control(s) while in flight so double-clicks can't double-post.
      var buttons = form.querySelectorAll('button:not([type="button"]), input[type="submit"]');
      for (var b = 0; b < buttons.length; b++) buttons[b].disabled = true;
      function reenable() {
        for (var i = 0; i < buttons.length; i++) buttons[i].disabled = false;
      }

      var action = form.getAttribute('action') || window.location.pathname;
      fetch(action, { method: 'POST', body: new FormData(form) })
        .then(function (res) {
          submitting.delete(form);
          // Success: Shopify followed its 302 to ?<type>_posted=true.
          if (res && res.url && SUCCESS_RE.test(res.url)) {
            revealSuccess(form);
          } else {
            // Field errors or bot /challenge: fall back to a real submit so Shopify's
            // server-rendered errors / challenge appear exactly as they do today.
            reenable();
            fallbackSubmit(form);
          }
        })
        .catch(function () {
          // Network error: let the native submit try (may itself reload with the result).
          submitting.delete(form);
          reenable();
          fallbackSubmit(form);
        });
    },
    true // capture phase — runs before the theme's listeners
  );

  function init() {
    revealOnLoad();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
